import Shepherd from 'shepherd.js';
import {
  isShepherdTourStepInteractive,
  type ShepherdStep,
} from './shepherd-interactive-step';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const SHEPHERD_TOUR_FIELD_LOCK_BODY_CLASS = 'shepherd-tour-highlight-fields-locked';

export const SHEPHERD_TOUR_DISMISS_BEFORE_FIELD_USE_MESSAGE =
  'Please dismiss this tour before using search or filters. Tap OK, Next, Skip, or the close (✕) button.';

/** Shown inside the Shepherd tip on search/filter lock steps. */
export const SHEPHERD_TOUR_FIELD_LOCK_TIP_SUFFIX = `<p class="shepherd-tour-field-lock-hint">${SHEPHERD_TOUR_DISMISS_BEFORE_FIELD_USE_MESSAGE}</p>`;

/** Appends the field-lock reminder below the step body copy. */
export function withTourFieldLockTipMessage(body: string): string {
  return `${body.trim()}${SHEPHERD_TOUR_FIELD_LOCK_TIP_SUFFIX}`;
}

const FIELD_LOCK_TARGET_CLASS = 'shepherd-tour-field-lock-target';

const EDITABLE_SELECTOR =
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]), textarea, select, .mat-mdc-select-trigger, .mat-select-trigger, [role="combobox"]';

/** Highlight visible; target fields locked until tour tip dismissed. */
export const SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS = {
  scrollTo: false as const,
  canClickTarget: false,
  floatingUIOptions: {
    strategy: 'fixed' as const,
  },
};

let notifyFieldBlocked: (() => void) | null = null;
const tourFieldLockDetachByTour = new WeakMap<ShepherdTour, () => void>();
let lockedTourRoot: HTMLElement | null = null;

function unlockFieldLockNode(node: Element): void {
  if (
    !(node instanceof HTMLInputElement ||
      node instanceof HTMLTextAreaElement ||
      node instanceof HTMLSelectElement)
  ) {
    return;
  }
  if (!node.hasAttribute('data-shepherd-lock-stored')) {
    return;
  }
  if (node instanceof HTMLSelectElement) {
    node.disabled = node.getAttribute('data-shepherd-was-disabled') === '1';
    node.removeAttribute('data-shepherd-was-disabled');
  } else {
    node.readOnly = node.getAttribute('data-shepherd-was-readonly') === '1';
    node.removeAttribute('data-shepherd-was-readonly');
  }
  node.removeAttribute('data-shepherd-lock-stored');
}

/** Clears every tour field lock in the DOM (OK / Skip / Finish / ✕ / tour end). */
export function releaseAllShepherdTourFieldLocks(): void {
  document.querySelectorAll('[data-shepherd-lock-stored]').forEach((node) => {
    unlockFieldLockNode(node);
  });
  document.querySelectorAll(`.${FIELD_LOCK_TARGET_CLASS}`).forEach((el) => {
    el.classList.remove(FIELD_LOCK_TARGET_CLASS);
  });
  document.body.classList.remove(SHEPHERD_TOUR_FIELD_LOCK_BODY_CLASS);
  lockedTourRoot = null;
}

export function setShepherdTourFieldLockNotifier(notify: () => void): void {
  notifyFieldBlocked = notify;
}

export function notifyShepherdTourFieldInteractionBlocked(): void {
  notifyFieldBlocked?.();
}

function isEditableTourFieldTarget(target: EventTarget | null, root: HTMLElement): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  if (!root.contains(target)) {
    return false;
  }
  if (target.closest('.shepherd-element, .shepherd-modal-overlay-container')) {
    return false;
  }
  return !!target.closest(EDITABLE_SELECTOR);
}

function releaseFieldLock(): void {
  if (lockedTourRoot) {
    applyFieldLockToRoot(lockedTourRoot, false);
  }
  releaseAllShepherdTourFieldLocks();
}

function applyFieldLockToRoot(root: HTMLElement | null, locked: boolean): void {
  if (!root) {
    return;
  }
  root.classList.toggle(FIELD_LOCK_TARGET_CLASS, locked);
  document.body.classList.toggle(SHEPHERD_TOUR_FIELD_LOCK_BODY_CLASS, locked);

  root.querySelectorAll('input, textarea, select').forEach((node) => {
    if (
      !(node instanceof HTMLInputElement ||
        node instanceof HTMLTextAreaElement ||
        node instanceof HTMLSelectElement)
    ) {
      return;
    }
    if (node instanceof HTMLInputElement) {
      const type = (node.type || 'text').toLowerCase();
      if (type === 'hidden' || type === 'button' || type === 'submit' || type === 'reset') {
        return;
      }
    }
    if (locked) {
      if (node.hasAttribute('data-shepherd-lock-stored')) {
        return;
      }
      node.setAttribute('data-shepherd-lock-stored', '1');
      if (node instanceof HTMLSelectElement) {
        node.setAttribute('data-shepherd-was-disabled', node.disabled ? '1' : '0');
        node.disabled = true;
        return;
      }
      node.setAttribute('data-shepherd-was-readonly', node.readOnly ? '1' : '0');
      node.readOnly = true;
      return;
    }
    unlockFieldLockNode(node);
  });
}

function blockTourFieldEvent(ev: Event, root: HTMLElement | null): void {
  if (!root || !isEditableTourFieldTarget(ev.target, root)) {
    return;
  }
  ev.preventDefault();
  ev.stopPropagation();
  if (typeof ev.stopImmediatePropagation === 'function') {
    ev.stopImmediatePropagation();
  }
  const target = ev.target;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    target.blur();
  }
  notifyShepherdTourFieldInteractionBlocked();
}

/**
 * While the tour tip is open on search/filter steps, block typing and dropdown use until OK / Skip / Next / Finish / ✕.
 */
export function registerTourHighlightedFieldLockGuards(
  tour: ShepherdTour,
  lockedStepIds: string[],
  resolveTourRoot: () => HTMLElement | null,
): void {
  const isLockedStepId = (stepId: string | undefined): boolean =>
    !!stepId && lockedStepIds.includes(stepId);

  const isTourFieldLockActive = (): boolean => {
    if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
      return false;
    }
    const current = tour.getCurrentStep?.() as ShepherdStep | undefined;
    const stepId = current?.id;
    return !!stepId && isLockedStepId(stepId) && isShepherdTourStepInteractive(tour, stepId);
  };

  const syncFieldLock = (): void => {
    if (!isTourFieldLockActive()) {
      releaseFieldLock();
      return;
    }
    const root = resolveTourRoot();
    if (!root) {
      return;
    }
    lockedTourRoot = root;
    applyFieldLockToRoot(root, true);
  };

  const attachListeners = (): void => {
    tourFieldLockDetachByTour.get(tour)?.();
    const onFocusIn = (ev: Event): void => {
      if (!isTourFieldLockActive()) {
        return;
      }
      blockTourFieldEvent(ev, resolveTourRoot());
    };
    const onPointerDown = (ev: Event): void => {
      if (!isTourFieldLockActive()) {
        return;
      }
      blockTourFieldEvent(ev, resolveTourRoot());
    };
    const onKeyDown = (ev: Event): void => {
      if (!isTourFieldLockActive()) {
        return;
      }
      if (!(ev instanceof KeyboardEvent)) {
        return;
      }
      if (ev.key === 'Tab' || ev.key === 'Escape') {
        return;
      }
      blockTourFieldEvent(ev, resolveTourRoot());
    };
    const onBeforeInput = (ev: Event): void => {
      if (!isTourFieldLockActive()) {
        return;
      }
      blockTourFieldEvent(ev, resolveTourRoot());
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('beforeinput', onBeforeInput, true);

    const detach = (): void => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('beforeinput', onBeforeInput, true);
      tourFieldLockDetachByTour.delete(tour);
    };
    tourFieldLockDetachByTour.set(tour, detach);
  };

  const teardown = (): void => {
    tourFieldLockDetachByTour.get(tour)?.();
    tourFieldLockDetachByTour.delete(tour);
    releaseAllShepherdTourFieldLocks();
  };

  tour.on('start', () => {
    attachListeners();
    queueMicrotask(syncFieldLock);
    requestAnimationFrame(syncFieldLock);
  });

  tour.on('show', (ev: { step?: ShepherdStep }) => {
    if (!isLockedStepId(ev?.step?.id)) {
      releaseFieldLock();
      return;
    }
    syncFieldLock();
    queueMicrotask(syncFieldLock);
    requestAnimationFrame(syncFieldLock);
  });

  tour.on('hide', (ev: { step?: ShepherdStep }) => {
    if (isLockedStepId(ev?.step?.id)) {
      releaseAllShepherdTourFieldLocks();
    }
  });

  tour.on('complete', teardown);
  tour.on('cancel', teardown);
}
